import UserAPI from "@api/User.api";
import CopyPassword from "@core-components/credentials/CopyPassword";
import CopyUsername from "@core-components/credentials/CopyUsername";
import GradientBorder from "@core-components/gradient-border/GradientBorder";
import SSPagination from "@core-components/pagination/Pagination";
import SSTooltip from "@core-components/tooltip/Tooltip";
import EPublicRoutes from "@core-types/routes.enum";
import useAppLanguage from "@hooks/language/useAppLanguage";
import useAppTranslation from "@hooks/language/useAppTranslation";
import { Flex, Group, Image, Stack, Table, Text } from "@mantine/core";
import OrderStatus from "@modules/order/components/after-buy/OrderStatus";
import COrderStatus from "@modules/order/models/COrderStatus";
import type IOrder from "@modules/order/models/IOrder";
import type { ExtendedBasketAccount } from "@modules/order/models/IOrder";
import { IconArrowRight } from "@tabler/icons-react";
import { type ReactNode, useEffect, useState } from "react";
import { Link } from "react-router";
import CoinsGainedBadge from "./CoinsGainedBadge";
import CopyText from "./CopyText";

const FIRST_PAGE_INDEX = 1;

const OrderTable = () => {
  const [orders, setOrders] = useState<IOrder[]>([]);
  const [pagination, setPagination] = useState({
    current: FIRST_PAGE_INDEX,
    total: 0,
    itemsPerPage: 0,
    pages: 0,
  });
  const { locationUrlBuilder } = useAppLanguage();
  const { t: tProduct } = useAppTranslation();

  useEffect(() => {
    getOrders(FIRST_PAGE_INDEX);
  }, []);

  const getOrders = (page: number) => {
    UserAPI.getOrders(page).then((orders) => {
      setPagination({
        current: orders.meta.currentPage,
        itemsPerPage: orders.meta.perPage,
        total: orders.meta.total,
        pages: Math.ceil(orders.meta.total / orders.meta.perPage),
      });
      setOrders(orders.data);
    });
  };

  const onPaginate = (current: number) => {
    setPagination((old) => ({ ...old, current }));
    getOrders(current);
  };

  const productDetails = (account: ExtendedBasketAccount) => {
    return (
      <Group className="uppercase">
        <Image
          src={account.imageUrl}
          className="aspect-square w-16 object-fill"
        />
        <Text className="w-40 whitespace-nowrap font-bold font-inter text-sm">
          {tProduct(`${account.i18nKey}.name`, { prefix: account.prefix })}
        </Text>
      </Group>
    );
  };

  const { t } = useAppTranslation("pages.clientDashboard.components");

  const rows = orders.map((order) => {
    const isPaid = order.status.isPaid();

    return order.accounts.map((item, index) => (
      <Table.Tr
        key={`${order.token}-${index}`}
        className="text-wrap border-b-0 font-inter font-semibold"
      >
        <Table.Td>{productDetails(item)}</Table.Td>
        <Table.Td>
          <CopyText
            variant="transparent"
            className="hover-scale !text-white p-0 text-base"
            notificationLabel={t("orderTable.orderIdLabel")}
            value={order.token}
          >
            {order.token.split("-")[0]}
          </CopyText>
        </Table.Td>
        <Table.Td>
          <CopyUsername username={item.username} isBlurred={!isPaid} />
        </Table.Td>
        <Table.Td>
          <CopyPassword password={item.password} isBlurred={!isPaid} />
        </Table.Td>
        <Table.Td>{item.price}€</Table.Td>
        <Table.Td>
          <CoinsGainedBadge coins={order.cashback} />
        </Table.Td>
        <Table.Td>{order.purchasedAt.format("DD/MM/YYYY")}</Table.Td>
        <Table.Td>
          <OrderStatus
            status={order?.status ?? new COrderStatus(COrderStatus.PENDING)}
          />
        </Table.Td>
        <Table.Td>
          <Link
            to={locationUrlBuilder(EPublicRoutes.ORDER, order.token)}
            className="no-underline"
          >
            <GradientBorder
              className="gradient-gray-to-light-white hover-scale rounded-[4px]"
              childrenClassName="p-1 py-[3px] !bg-[#202125]"
            >
              <Group gap={2} wrap="nowrap" justify="center">
                <Text className="m-0 p-0 text-xs">
                  {t("orderTable.viewOrder")}
                </Text>
                <IconArrowRight size={20} />
              </Group>
            </GradientBorder>
          </Link>
        </Table.Td>
      </Table.Tr>
    ));
  });

  const Th = ({
    children,
    className,
  }: { children: ReactNode; className?: string }) => {
    return (
      <Table.Th
        className={`border-primary-light border-b-[1px] border-solid font-normal text-sm ${className}`}
      >
        {children}
      </Table.Th>
    );
  };

  const noContent = (
    <Flex
      justify="center"
      align="center"
      className="sticky left-0 h-[150px] w-[85vw] bg-primary-client text-center text-white lg:w-full"
    >
      {t("orderTable.noContent")}
    </Flex>
  );

  return (
    <Stack>
      <div className="w-full overflow-auto">
        <Table className="min-w-[1400px] border-separate rounded-md border-[1px] border-primary-light border-solid bg-primary-client text-primary-white">
          <Table.Thead className="h-14 bg-primary-background text-primary-dark-text">
            <Table.Tr>
              <Th className="rounded-tl-rounded-md">
                {t("orderTable.headers.orders")}
              </Th>
              <Th>{t("orderTable.headers.id")}</Th>
              <Th className="w-fit">{t("orderTable.headers.identifier")}</Th>
              <Th>{t("orderTable.headers.password")}</Th>
              <Th>{t("orderTable.headers.price")}</Th>
              <Th>
                <SSTooltip
                  label={t("orderTable.tooltip.coins")}
                  text={[t("orderTable.tooltip.description")]}
                />
              </Th>
              <Th>{t("orderTable.headers.date")}</Th>
              <Th>{t("orderTable.headers.status")}</Th>
              <Th className="rounded-tr-rounded-md">
                {t("orderTable.headers.informations")}
              </Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
          {!rows.length && <Table.Td colSpan={9}>{noContent}</Table.Td>}
        </Table>
      </div>

      <SSPagination
        currentPage={pagination.current}
        itemsCount={pagination.total}
        itemsPerPage={pagination.itemsPerPage}
        pageCount={pagination.pages}
        onPaginate={onPaginate}
      />
    </Stack>
  );
};

export default OrderTable;
